import {
  FeatureLayer,
  LayerVariantSelectors,
  LayerVariantValues,
} from "src/managers/WmsLayerFunc";
import { Localization } from "@viamap/viamap2-common";
import {
  FilterSpec,
  FilterType,
  FilterValue,
} from "src/states/ExploreSearchState";
import { ColorBoxEditableV2 } from "./ComponentUtils";
import { OpacityAdjuster, Slider } from "./MitInputComponents";
import { ADModalBody, AdvancedDragModal } from "src/componentsUtils/AdvancedDragModal";
import { GlassCheckbox } from "./MitGlassComponents";
import { Fragment } from "react/jsx-runtime";
import { MIBIcon } from "src/componentsUtils/MIBIcon";
import { useEffect, useState } from "react";
import { DC } from "src/dataCollector/DataCollector";
import { GlassButton } from "./GlassButtons";
import { ActionTracker } from "src/common/managers/ActionTracker";

type props<T extends string[]> = {
  showWindow: number;
  layers: Record<T[number], FeatureLayer>;
  activeLayers: T;
  editingLayers: T;
  //
  callBackToSetVisibility: (key: T[number], show: boolean) => void;
  onChange: (key: T[number], value: any) => void;
  callBackToHideWindow: () => void;
  callBackToStopEditing: (key: T[number]) => void;
};

/**
 * @abrevation FLE
 * @param props
 * @returns
 */
export function FeatureLayerEditor<T extends string[]>(props: props<T>) {

  function closeAll() {
    props.callBackToHideWindow();
  }

  function onChange(a, b) {
    props.onChange(a, b);
  }

  function close(key: T[number]) {
    props.callBackToStopEditing(key);
  }

  if ((props.editingLayers || []).length == 0) {
    return <></>;
  }

  return (
    <AdvancedDragModal title={Localization.getText("Layer Settings")}
      onClose={closeAll}
      PosDefault={{ width: "165px" }}
      variant={["Small", "NSWhite"]}
    >
      <ADModalBody>
        {props.editingLayers.map((a) => {
          return (
            <FeatureLayerEditorSingle
              key={a}
              layerKey={a}
              layer={props.layers[a]}
              close={(key) => close(key)}
              onChange={onChange}
              activeLayers={props.activeLayers}
              callBackToSetVisibility={props.callBackToSetVisibility}
            />
          );
        })}
      </ADModalBody>
    </AdvancedDragModal>
  );
}

function FeatureLayerEditorSingle(props: {
  layerKey: string;
  layer: FeatureLayer;
  close: (key: string) => void;
  onChange: (key: string, val: any) => any;

  activeLayers: string[];
  callBackToSetVisibility: (key: string, checked: boolean) => any;
}) {
  function onChange(val) {
    props.onChange(props.layerKey, val);
  }

  const lang = Localization.getLanguage();
  const layer = props.layer;

  return (
    <div className="FeatureLayerEditor_Single FLE-S">
      <div className="FLE-Head">
        <GlassCheckbox
          checked={(props.activeLayers || []).includes(props.layerKey)}
          onClick={(a, b) => props.callBackToSetVisibility(props.layerKey, b)}
        />
        <span className="FLE-Title">
          {layer.layer.translationTable?.[lang]?.[layer.layer.label] ||
            layer.layer.label}
        </span>
        <FLECloseBtn onClick={() => props.close(props.layerKey)} />
      </div>
      <div className="FLE-Body">
        {props.layer.getType() === "WMS" ? (
          props.layer.layer["noColor"] ?
            <WMSstylingNoColor values={layer.layer.variantValues} onChange={onChange} />
            :
            <WMSstyling values={layer.layer.variantValues} onChange={onChange} />
        ) : null}
        <VariantSelectorsParser
          parse={layer.layer.variantSelectors}
          values={layer.layer.variantValues}
          onChange={onChange}
        />
        {props.layer.getType() === "geojsonWFS" ? (
          <GeoJsonWFSFiltering
            layer={props.layer}
            onChange={(val) => {
              console.log(val);
              onChange({ userFilter: val })
            }}
          />
        ) : null}
      </div>
    </div>
  );
}

export function GeoJsonWFSFiltering(props: {
  layer: FeatureLayer;
  // filter: any;
  onChange: (val: any) => any;
}) {

  const [currentValue, setCurrentValue] = useState(props.layer.layer.variantValues?.["variantFilter"] || props.layer.layer.variantSelectors?.["variantFilter"]?.default || []);
  const [description, setDescription] = useState<{ [key: string]: string }>({});

  useEffect(() => {
    props.onChange(
      currentValue
    );
  }, [currentValue])

  useEffect(() => {
    if (props.layer.layer["wfs"]?.key && props.layer.layer["wfs"]?.type) {
      const abortController = new AbortController();
      DC.WFS.getFeatureType(
        props.layer.layer["wfs"].key,
        props.layer.layer["wfs"].type
      ).then((a) => {
        if (abortController.signal.aborted) return;
        setDescription(a);
      });
      return () => {
        abortController.abort();
      }
    }
  }, [])

  if (description["error"]?.length) {
    <></>
  }

  if (!Object.keys(description || {}).length) {
    return (
      <div style={{ textAlign: "center", padding: "20px" }}>
        <span>{Localization.getText("Loading")}...</span>
      </div>
    );
  }

  return (
    <>
      <InputGroup className="FLE-UserFilter" title={currentValue.length ? Localization.getText("Filter") : ""}>
        {[...currentValue].map((a, ind) => {
          return (
            <Fragment key={ind}>
              <LayerFilterBuilder
                valueDescription={description}
                filter={a}
                onChange={(val) => {
                  console.log(val);
                  setCurrentValue((old) => {
                    if (val !== null) {
                      if (old[ind]) {
                        return old.map((b, i) => i == ind ? val : b);
                      } else {
                        return [...old, val];
                      }
                    } else {
                      ActionTracker.track("FeatureEditor", "FilterRemoved", "RemoveFilter", { layerID: props?.layer?.layer?.label || "unknown" });
                      return old.filter((b, i) => i != ind);
                    }
                  })
                }}
              />
            </Fragment>
          )
        })}
        <GlassButton style={{ "marginBlock": "8px" }} onClick={() => {
          ActionTracker.track("FeatureEditor", "FilterAdded", "AddFilter", { layerID: props?.layer?.layer?.label || "unknown" });
          setCurrentValue((old) => [...old, ["==", ["get", Object.keys(description)[0]], ""]]);
        }} >{Localization.getText("New filter")}</GlassButton>
      </InputGroup>
    </>
  )
}

const simpleFilterOptions = ["==", "!=", ">", "<", ">=", "<="] as const;
const simpleFilterOptionsLabels: { [K in typeof simpleFilterOptions[number]]: { symbol: string; da: string; en: string } } = {
  "==": { symbol: "=", da: "er lig med", en: "is equal to" },
  "!=": { symbol: "!=", da: "er ikke lig med", en: "is not equal to" },
  ">": { symbol: ">", da: "er større end", en: "is greater than" },
  "<": { symbol: "<", da: "er mindre end", en: "is less than" },
  ">=": { symbol: ">=", da: "er større end eller lig med", en: "is greater than or equal to" },
  "<=": { symbol: "<=", da: "er mindre end eller lig med", en: "is less than or equal to" },
} as const;

export function LayerFilterBuilder(props: {
  valueDescription: { [key: string]: string };
  filter: any;
  onChange: (val: any) => any;
}) {

  const [operator, setOperator] = useState<string | null>(props.filter ? props.filter[0] : "==");
  const [field, setField] = useState<string | null>(props.filter ? props.filter[1]?.[1] : props.valueDescription ? Object.keys(props.valueDescription)[0] : null);
  const [value, setValue] = useState<string | null>(props.filter ? props.filter[2] + "" : "");

  function handleChange(operator: string, field: string, value: string) {
    let type = props.valueDescription[field];
    function parseType(val: string, type: string) {
      if (type == "number") {
        return Number(val) || 0;
      } else if (type == "boolean") {
        return val == "true";
      } else {
        return val;
      }
    }
    props.onChange([operator, ["get", field], parseType(value, type)]);
  }

  function handleChangeNull() {
    props.onChange(null);
  }

  return <>
    <div style={{ display: "flex", gap: "4px", marginTop: "8px" }}>
      <select style={{ flex: "1 1 0%", minWidth: 0, textOverflow: "ellipsis" }} value={
        field || ""
      } onChange={(e) => {
        setField(e.target.value);
        if (e.target.value && operator && value) {
          handleChange(operator, e.target.value, value);
        }
      }}>
        <option value="" disabled>Vælg felt</option>
        {Object.keys(props.valueDescription).map((a) => (
          <option key={a} value={a}>{a}</option>
        ))}
      </select>
      <button>
        <MIBIcon icon="remove" onClick={handleChangeNull} />
      </button>
    </div>
    <div style={{ display: "flex", gap: "4px", marginTop: "8px" }}>
      <select
        title={
          simpleFilterOptionsLabels[operator || "=="][Localization.getLanguage()] || operator
        }
        style={{ width: "fit-content" }}
        value={operator || "=="}
        onChange={(e) => {
          setOperator(e.target.value);
          if (field && value) {
            handleChange(e.target.value, field, value);
          }
        }}
      >
        {simpleFilterOptions.map((a) => (
          <option key={a} value={a}>{simpleFilterOptionsLabels[a].symbol}</option>
        ))}
      </select>
      <input type="text" value={value || ""} style={{ flex: "1 1 0%", minWidth: 0 }} onChange={(e) => { setValue(e.target.value); handleChange(operator || "==", field || "", e.target.value); }} />
    </div>
  </>
}

export function FLECloseBtn(props: { onClick: () => void }) {
  return (
    <div onClick={props.onClick} className="FLE-close">
      <MIBIcon icon="closeSingle" />
    </div>
  );
}

type VariantSelectorsParserProps = {
  onChange: (value: any) => any;
  parse?: LayerVariantSelectors;
  values?: LayerVariantValues;
};
export function VariantSelectorsParser(props: VariantSelectorsParserProps) {
  let keys = Object.keys(props.parse || {});

  function onChange(key: string, value: any) {
    props.onChange({
      ...props.values,
      [key]: value,
    });
  }

  return (
    <>
      {/* {props.values?.["opacity"] as unknown as number ?? 100} */}
      {keys.map((a: keyof LayerVariantSelectors) => {
        let filter: FilterSpec | undefined = props.parse?.[a];
        let value = props.values?.[a] || undefined;
        if (typeof a === "string")
          return (
            <Fragment key={a}>
              <FilterSpecInput
                key={a}
                filter={filter}
                onChange={(val) => onChange(a, val)}
                value={value}
              />
            </Fragment>
          );
        return <Fragment key={a} />;
      })}
    </>
  );
}

type WMS_StylingProps = {
  onChange: (value: any) => any;
  parse?: LayerVariantSelectors;
  values?: LayerVariantValues;
};
function WMSstyling(props: WMS_StylingProps) {
  function onChange(key: string, value: any) {
    props.onChange({
      ...props.values,
      [key]: value,
    });
  }

  return (
    <>
      <InputGroup title={Localization.getText("Transparency")}>
        <OpacityAdjuster
          value={props.values?.["raster-opacity"]?.[0] ?? 1}
          onChange={(val) => onChange("raster-opacity", [val])}
        />
      </InputGroup>
      <InputGroup title={Localization.getText("Saturation")}>
        <Slider
          value={props.values?.["raster-saturation"]?.[0] ?? 0}
          onChange={(val) => onChange("raster-saturation", [val])}
          step={0.05}
          min={-1}
          max={1}
        />
      </InputGroup>
      <InputGroup title={Localization.getText("Hue")}>
        <Slider
          value={props.values?.["raster-hue-rotate"]?.[0] ?? 0}
          onChange={(val) => onChange("raster-hue-rotate", [val])}
          step={1}
          min={0}
          max={360}
        />
      </InputGroup>
    </>
  );
}

function WMSstylingNoColor(props: WMS_StylingProps) {
  function onChange(key: string, value: any) {
    props.onChange({
      ...props.values,
      [key]: value,
    });
  }

  return (
    <>
      <InputGroup title={Localization.getText("Transparency")}>
        <OpacityAdjuster
          value={props.values?.["raster-opacity"]?.[0] ?? 1}
          onChange={(val) => onChange("raster-opacity", [val])}
        />
      </InputGroup>
    </>
  );
}


export function InputGroup(props: { title, children, style?: any, className?: string }) {
  return (
    <div className={`FLE-InputGroup ${props.className || ""}`} style={props.style}>
      <div className="FLE-InputTitle">{props.title}</div>
      <div className="FLE-Input">{props.children}</div>
    </div>
  );
}

export function FilterSpecInput(props: {
  filter: FilterSpec | undefined;
  value?: FilterValue;
  onChange?: (a: any) => void;
}) {
  const lang = Localization.getLanguage();
  const value = props.value ?? props.filter?.default ?? [];
  const title =
    props.filter?.translationTable?.[lang]?.[props.filter.title] ||
    props.filter?.title ||
    "";
  const transVal =
    props.filter?.translationTable?.[lang]?.[value[0]] ||
    props.filter?.options?.[value[0]] ||
    value[0] ||
    "";

  if (props.filter == undefined) {
    return <></>;
  }

  switch (props.filter.filterType) {
    case FilterType.SelectTextSlider:
      return (
        <InputGroup title={title}>
          <div>{transVal}</div>
          <Slider
            value={Object.keys(props.filter.options || {}).findIndex(
              (a) => a == value[0]
            )}
            onChange={(a) =>
              props.onChange?.([Object.keys(props?.filter?.options || {})[a]])
            }
            step={1}
            min={0}
            max={
              Number(Object.keys(props.filter?.options || {}).length || 0) - 1
            }
          />
        </InputGroup>
      );

    case FilterType.SingleSelect:
      return (
        <InputGroup title={title}>
          {Object.keys(props.filter.options || {}).map((a) => (
            <GlassCheckbox
              key={a}
              className="radio"
              onChange={() => props.onChange?.([a])}
              checked={value[0] == a}
            >
              {props.filter?.translationTable?.[lang]?.[a] ||
                props.filter?.options?.[a] ||
                a ||
                ""}
            </GlassCheckbox>
          ))}
        </InputGroup>
      );
    case FilterType.SelectText:
    case FilterType.SelectTextList:
    case FilterType.Select:
      return (
        <InputGroup title={title}>
          {Object.keys(props.filter.options || {}).map((a) => (
            <GlassCheckbox
              key={a}
              onClick={(ev, l) => {
                props.onChange?.([
                  ...value.filter((b) => a !== b),
                  ...(l ? [a] : []),
                ]);
              }}
              checked={(value || []).includes(a) || false}
              value={a}
            >
              {props.filter?.translationTable?.[lang]?.[a] ||
                props.filter?.options?.[a] ||
                a ||
                ""}
            </GlassCheckbox>
          ))}
        </InputGroup>
      );
    case FilterType.Number:
      return (
        <InputGroup title={title}>
          <div>{transVal}</div>
          <Slider
            value={value[0]}
            onChange={(a) =>
              props.onChange?.([a])
            }
            step={props.filter?.["step"] ?? 0.05}
            max={props.filter?.["max"] ?? 1}
            min={props.filter?.["min"] ?? 0}
          />
        </InputGroup>
      );
    case FilterType.Color:
      return (
        <InputGroup title={title} style={{ paddingBottom: "8px" }}>
          <ColorBoxEditableV2 color={value[0]} onCommit={(a) => props.onChange?.([a])} />
        </InputGroup>
      );
    case FilterType.Boolean:
      return (
        <InputGroup title={title}>
          <GlassCheckbox
            // variant={"normal"}
            checked={(props.filter?.true == value[0]) || false}
            onClick={(ev, checked) => props.onChange?.(checked ? props.filter?.true : props.filter?.false)}
          >
            {title}
          </GlassCheckbox>
        </InputGroup>
      );
    case FilterType.String:
    case FilterType.DateRange:
    case FilterType.IntegerRange:
    case FilterType.FilterType:
    case FilterType.Integer:
    case FilterType.StaticValue:
    case FilterType.IntegerSet:
    case FilterType.BoundingBox:
    case FilterType.Polygon:
    case FilterType.StringInNestedObjectList:
      break;
  }
  return <></>;
}
